/**
 * Hook for the org "looked-up emission factor" library (save-on-use).
 *
 * `useSaveEmissionFactorPick` snapshots a chosen Climatiq factor into the org's
 * per-sector library table (`climatiq_<sector>`, named by sector e.g. "Transport"; data_source 'Climatiq') via
 * the backend (Pattern C → save_org_emission_factor), which also accrues the
 * dimension-column metadata so the factor is usable in the activity form. Saved
 * snapshots are first-class local `carbon_emission_factors` rows, so they
 * resurface in the picker's "local" provider search for reuse next time.
 *
 * (A per-row auto-suggest that resolves a saved snapshot by its dimension key and
 * pre-fills the CO₂e cell is a planned follow-up — see docs/EMISSIONS_PROXY.md.)
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "@/contexts/OrganizationContext";
import { authedPost } from "@/lib/api";
import { toast } from "@/hooks/useToast";
import { invalidateEmissionFactorCaches } from "@/lib/emissionFactorCacheKeys";
import type { SaveEmissionFactorInput } from "@releve/shared";

export function useSaveEmissionFactorPick() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation<{ factor_id: string }, Error, SaveEmissionFactorInput>({
    mutationFn: async (input) => {
      if (!orgId) throw new Error("No organization selected");
      // emissions is served outside the typed Hono client (see apps/api/src/
      // index.ts), so call it via the shared authed fetch helper.
      return authedPost<{ factor_id: string }>(
        `/api/v1/emissions/orgs/${encodeURIComponent(orgId)}/factors`,
        input
      );
    },
    // `save_org_emission_factor` writes a factor row AND accrues the table's
    // dimension-column metadata, so it moves the tables index (its rollup
    // counts), the factors index and the picker's own cascade caches. The hook
    // used to invalidate NOTHING — the picker's caller happened to invalidate
    // some of it inline — so a snapshot saved from anywhere else went unseen.
    onSuccess: () => {
      invalidateEmissionFactorCaches(queryClient, orgId);
    },
    onError: () => {
      toast.errorTemplates.generic();
    },
  });
}
